import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../../api/client'
import type { ProposalConfirmation, ProposalDetail, ProposalLine, UpdateLineResponse } from '../../api/types'
import { useStores } from '../../app/StoreContext'
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorBox,
  Num,
  Spinner,
  formatDate,
  formatDateTime,
} from '../../ui/primitives'
import { AddLinePanel } from './AddLinePanel'
import { ContainerPanel } from './ContainerPanel'
import { GridHead, GridSummary, useGrid, type ColumnDef } from '../../ui/grid'
import { ThresholdProgress } from './ThresholdProgress'
import { channelLabel, formatPacks, formatPercent } from './shared'

/**
 * Dettaglio della proposta: dove si decide davvero cosa ordinare.
 *
 * Due scelte guidano tutta la pagina. La prima: il calcolo è del motore di previsione e non lo
 * rifacciamo qui, quindi dopo ogni modifica si rilegge il dato dal server e si mostra quello che è
 * stato registrato — le quantità vengono arrotondate al multiplo di confezione e l'utente deve
 * vedere il valore vero, non quello che ha digitato. La seconda: le regole di business le conosce
 * il server, quindi non indoviniamo in anticipo cosa è modificabile: se risponde 422 lo diciamo
 * accanto alla riga e riportiamo il campo com'era.
 */
export function ProposalDetailPage() {
  const { id } = useParams()
  const proposalId = Number(id)
  const { storeName } = useStores()

  const [detail, setDetail] = useState<ProposalDetail>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [reloadToken, setReloadToken] = useState(0)

  // Righe toccate in questa sessione: restano evidenziate finché si resta sulla pagina.
  const [changed, setChanged] = useState<string[]>([])
  const [savingItem, setSavingItem] = useState<string>()
  const [lineProblems, setLineProblems] = useState<Record<string, string>>({})
  /** Cambia a ogni salvataggio concluso: rimette nei campi il valore che il server ha registrato. */
  const [revision, setRevision] = useState(0)

  /**
   * Vero quando la pagina non è più in cima: il riquadro di validazione si riduce alla sua frase.
   *
   * <p>La soglia a 24 pixel e non a zero evita il tremolio: ridursi cambia l'altezza della pagina,
   * e con la soglia a zero il documento si accorcerebbe fino a tornare in cima, dove il riquadro
   * si riespande — e da lì un rimbalzo continuo. Ventiquattro pixel sono più del salto che la
   * riduzione produce.</p>
   */
  const [compatta, setCompatta] = useState(false)

  useEffect(() => {
    const guarda = () => setCompatta(window.scrollY > 24)
    guarda()
    window.addEventListener('scroll', guarda, { passive: true })
    return () => window.removeEventListener('scroll', guarda)
  }, [])

  const [removingItem, setRemovingItem] = useState<string>()
  const [discarding, setDiscarding] = useState(false)

  const [confirming, setConfirming] = useState(false)
  const [confirmation, setConfirmation] = useState<ProposalConfirmation>()
  const [confirmRejected, setConfirmRejected] = useState(false)
  const [confirmError, setConfirmError] = useState<unknown>()

  useEffect(() => {
    if (!Number.isFinite(proposalId)) return

    let cancelled = false
    setLoading(true)
    setError(undefined)

    api
      .get<ProposalDetail>(`/order-proposals/${proposalId}`)
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e)
        setDetail(undefined)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [proposalId, reloadToken])

  const reload = () => setReloadToken((t) => t + 1)

  /** Toglie una riga aggiunta a mano. Le righe del motore non si cancellano: si azzerano. */
  const removeLine = async (item: string) => {
    setRemovingItem(item)
    try {
      setDetail(await api.del<ProposalDetail>(
        `/order-proposals/${proposalId}/lines/${encodeURIComponent(item)}`))
      setChanged((c) => c.filter((x) => x !== item))
    } catch (e: unknown) {
      setLineProblems((previous) => ({
        ...previous,
        [item]: e instanceof ApiError ? (e.detail ?? e.title) : 'Rimozione non riuscita.',
      }))
    } finally {
      setRemovingItem(undefined)
    }
  }

  /**
   * Scarta la proposta con una motivazione obbligatoria: è l'unica traccia che resta della
   * decisione, e nel sistema precedente la proposta veniva cancellata senza lasciarne nessuna.
   */
  const discardProposal = async () => {
    const reason = window.prompt(
      [
        'Perché questa proposta non viene ordinata?',
        '',
        'La motivazione resta registrata insieme alla proposta.',
      ].join('\n'),
    )
    if (reason === null) return

    if (reason.trim().length < 3) {
      window.alert('Serve una motivazione di almeno tre caratteri.')
      return
    }

    setDiscarding(true)
    try {
      await api.post(`/order-proposals/${proposalId}/discard`, { reason: reason.trim() })
      reload()
    } catch (e: unknown) {
      setConfirmError(e)
    } finally {
      setDiscarding(false)
    }
  }

  const saveLine = async (item: string, patch: { requestedQuantity?: number; addedQuantity?: number }) => {
    setSavingItem(item)
    setLineProblems((previous) => {
      const next = { ...previous }
      delete next[item]
      return next
    })

    try {
      // Il server arrotonda al multiplo di confezione e restituisce la riga registrata insieme
      // ai totali ricalcolati: si aggiorna in memoria quel che serve, senza riscaricare tutte
      // le righe della proposta a ogni ritocco.
      const updated = await api.patch<UpdateLineResponse>(
        `/order-proposals/${proposalId}/lines/${encodeURIComponent(item)}`,
        patch,
      )

      setDetail((current) =>
        current ?
          {
            ...current,
            lines: current.lines.map((l) => (l.item === item ? updated.line : l)),
            validation: updated.validation,
          }
        : current,
      )
      setChanged((previous) => (previous.includes(item) ? previous : [...previous, item]))
    } catch (e) {
      const message =
        e instanceof ApiError ?
          e.detail || e.title
        : 'Modifica non riuscita. Riprova fra qualche istante.'
      setLineProblems((previous) => ({ ...previous, [item]: message }))
    } finally {
      setSavingItem(undefined)
      // Anche dopo un errore: il campo deve tornare al valore registrato lato server.
      setRevision((r) => r + 1)
    }
  }

  const confirmProposal = async () => {
    const ok = window.confirm(
      'Confermare la proposta? La proposta viene chiusa e trasmessa: l’operazione non è reversibile.',
    )
    if (!ok) return

    setConfirming(true)
    setConfirmation(undefined)
    setConfirmRejected(false)
    setConfirmError(undefined)

    try {
      // La conferma risponde sempre 200: l'esito è nel corpo, perché una proposta rifiutata
      // è un risultato di dominio, non un errore di protocollo.
      const result = await api.post<ProposalConfirmation>(`/order-proposals/${proposalId}/confirm`)
      setConfirmation(result)
      setConfirmRejected(!result.confirmed)
    } catch (e) {
      setConfirmError(e)
    } finally {
      setConfirming(false)
      reload()
    }
  }

  // La giacenza CEDI non arriva sulle proposte a fornitore diretto: mostrare una colonna
  // sempre vuota toglie spazio alle altre su uno schermo stretto. Si nasconde quando nessuna
  // riga la valorizza, e ricompare da sola sulle proposte in cui il dato c'è.
  const showCediStock = useMemo(
    () => detail?.lines.some((l) => l.cediStock !== null && l.cediStock !== undefined) ?? false,
    [detail],
  )

  if (!Number.isFinite(proposalId)) {
    return <Empty>Identificativo della proposta non valido.</Empty>
  }

  // Come si legge ogni colonna per ordinare e filtrare. Il corpo della tabella resta quello che
  // era: le celle hanno campi di quantità e pastiglie, e generarle toglierebbe più di quanto dia.
  const columns = useMemo<ColumnDef<ProposalLine>[]>(
    () => [
      { key: 'item', label: 'Articolo', value: (l) => l.item },
      { key: 'description', label: 'Descrizione', value: (l) => l.description },
      { key: 'packSize', label: 'Conf.', value: (l) => l.packSize, numeric: true,
        title: 'Pezzi per confezione' },
      { key: 'sold', label: 'Venduto 10gg', value: (l) => l.soldLast10Days, numeric: true,
        title: 'Venduto negli ultimi 10 giorni' },
      { key: 'storeStock', label: 'Giac. PV', value: (l) => l.storeStock, numeric: true },
      ...(showCediStock ?
        [{ key: 'cediStock', label: 'Giac. CEDI', value: (l: ProposalLine) => l.cediStock, numeric: true }]
      : []),
      { key: 'incoming', label: 'In arrivo', value: (l) => l.incomingQuantity, numeric: true },
      { key: 'suggested', label: 'Suggerita', value: (l) => l.suggestedQuantity, numeric: true,
        title: 'Quantità calcolata dal motore di previsione' },
      { key: 'requested', label: 'Richiesta', value: (l) => l.requestedQuantity, numeric: true },
      { key: 'added', label: 'Aggiunta', value: (l) => l.addedQuantity, numeric: true },
      { key: 'special', label: 'Speciale', value: (l) => l.specialQuantity, numeric: true,
        title: 'Quantità proveniente da ordini speciali' },
      { key: 'total', label: 'Totale pezzi', value: (l) => l.totalQuantity, numeric: true },
      { key: 'packs', label: 'Colli', value: (l) => l.packCount, numeric: true,
        title: 'Confezioni intere corrispondenti al totale' },
    ],
    [showCediStock],
  )

  const grid = useGrid(detail?.lines ?? [], columns)

  if (loading && !detail) return <Spinner label="Caricamento della proposta…" />
  if (error !== undefined && !detail) return <ErrorBox error={error} onRetry={reload} />
  if (!detail) return <Empty>Proposta non disponibile.</Empty>

  const { header, lines, validation } = detail
  const editable = header.status === 'Open'
  const simulated = confirmation?.orderNumber?.startsWith('SIMULATO') ?? false

  return (
    <>
      <div className="page-head">
        <div>
          <div className="muted">
            <Link to="/proposals">← Torna all’elenco</Link>
          </div>
          <h1>
            Proposta {header.id} · {header.supplierName}
          </h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Punto vendita {header.store} — {storeName(header.store)} · fornitore {header.supplier} ·{' '}
            {channelLabel(header.channel, header.sourceCedi)} · previsione del {formatDate(header.forecastDate)}
          </p>
        </div>

        <div className="page-tools">
          <Badge tone={header.status === 'Open' ? 'info' : header.status === 'Discarded' ? 'muted' : 'ok'}>
            {header.status === 'Open' ?
              'Aperta'
            : header.status === 'Confirmed' ?
              'Confermata'
            : header.status === 'Dispatched' ?
              'Trasmessa'
            : 'Scartata'}
          </Badge>
          <Button onClick={reload} disabled={loading || confirming}>
            Ricarica
          </Button>
          <Button
            variant="primary"
            onClick={confirmProposal}
            disabled={!editable || !validation.canConfirm || confirming || savingItem !== undefined}
            title={
              !editable ? 'La proposta non è più aperta.'
              : !validation.canConfirm ? 'Ci sono problemi da risolvere prima di confermare.'
              : 'Conferma e trasmetti la proposta'
            }
          >
            {confirming ? 'Conferma in corso…' : 'Conferma proposta'}
          </Button>
          <Button
            variant="danger"
            onClick={() => void discardProposal()}
            disabled={!editable || discarding || confirming}
            title={
              editable ?
                'Registra la decisione di non ordinare, con la motivazione'
              : 'La proposta non è più aperta.'
            }
          >
            {discarding ? 'Scarto in corso…' : 'Scarta'}
          </Button>
        </div>
      </div>

      {/* Esito della conferma: il caso più delicato è la trasmissione simulata, che non deve
          essere scambiata per un ordine partito davvero. */}
      {confirmation?.confirmed && simulated && (
        <div className="notice notice-warn">
          <strong>Proposta confermata — trasmissione SIMULATA</strong>
          <p>
            Riferimento <span className="num">{confirmation.orderNumber}</span>. Nessun ordine è stato
            inviato al fornitore e nessun documento è stato creato sul gestionale: la proposta è
            registrata in KPO e resta in attesa di trasmissione.
          </p>
        </div>
      )}

      {confirmation?.confirmed && !simulated && confirmation.orderNumber && (
        <div className="notice notice-ok">
          <strong>Proposta confermata e trasmessa</strong>
          <p>
            Numero d’ordine <span className="num">{confirmation.orderNumber}</span>.
          </p>
        </div>
      )}

      {confirmation?.confirmed && !confirmation.orderNumber && (
        <div className="notice notice-warn">
          <strong>Proposta confermata, ma non trasmessa</strong>
          {confirmation.problems.length > 0 && (
            <ul>
              {confirmation.problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirmation && !confirmation.confirmed && (
        <div className="notice notice-warn">
          <strong>Conferma non eseguita</strong>
          <ul>
            {confirmation.problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {confirmRejected && (
        <div className="notice notice-warn">
          <strong>Conferma rifiutata dal server</strong>
          <p>
            La proposta non rispetta ancora tutte le regole. I motivi aggiornati sono nel riquadro di
            validazione qui sotto.
          </p>
        </div>
      )}

      {confirmError !== undefined && <ErrorBox error={confirmError} onRetry={confirmProposal} />}

      {/* Riquadro di validazione: sempre in testa, perché è la risposta alla domanda "posso
          confermare?" ed è il motivo per cui si apre questa pagina.

          Scorrendo si riduce alla sola frase verde (o rossa) e resta appiccicato in alto: quella
          frase è la risposta, i quattro numeri sono il ragionamento che la sostiene — utili quando
          si guarda la testa della pagina, ingombro quando si lavora sulle righe. Tornando in cima
          ricompare tutto. */}
      <div className={compatta ? 'validazione-compatta' : undefined}>
      <Card title={compatta ? '' : 'Validazione'}>
        {/* A proposta chiusa la domanda "posso confermare?" non ha più senso: quello che conta è
            lo stato in cui si trova, e i totali qui sotto restano validi come consuntivo. */}
        {!editable ?
          <div className="notice notice-info" style={{ marginBottom: 16 }}>
            <strong>
              {header.status === 'Dispatched' ?
                'Proposta già confermata e trasmessa: non è più modificabile.'
              : header.status === 'Confirmed' ?
                'Proposta già confermata: resta in attesa di trasmissione e non è più modificabile.'
              : 'Proposta scartata: non è più modificabile.'}
            </strong>
            {header.confirmedBy && (
              <p>
                Confermata da {header.confirmedBy} il {formatDateTime(header.confirmedAt)}.
              </p>
            )}
          </div>
        : <div className={`notice notice-${validation.canConfirm ? 'ok' : 'warn'}`} style={{ marginBottom: 16 }}>
            <strong>
              {validation.canConfirm ?
                'La proposta è confermabile.'
              : 'La proposta non è confermabile: ci sono punti da risolvere.'}
            </strong>
            {!validation.canConfirm && (
              <ul>
                {validation.problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            )}
          </div>
        }

        <div className="grid grid-4" style={{ marginBottom: 16, display: compatta ? 'none' : undefined }}>
          <div className="stat">
            <div className="stat-label">Verso il minimo d’ordine</div>
            <div className="stat-value num">
              {typeof validation.thresholdPercent === 'number' ?
                formatPercent(validation.thresholdPercent)
              : <span className="muted">—</span>}
            </div>
            <div className="stat-note">
              {validation.thresholdUnknown || typeof validation.thresholdPercent !== 'number' ?
                'soglia non impostata'
              : validation.thresholdPercent >= 100 ?
                'soglia raggiunta'
              : `manca ${formatPercent(100 - validation.thresholdPercent)}`}
            </div>
          </div>

          <div className="stat">
            <div className="stat-label">Colli</div>
            <div className="stat-value num">{formatPacks(validation.totalPacks)}</div>
            <div className="stat-note">
              {typeof validation.requiredPacks !== 'number' ?
                'soglia in colli non impostata'
              : validation.requiredPacks > 0 ?
                `minimo ${formatPacks(validation.requiredPacks)} colli`
              : 'nessun minimo in colli'}
            </div>
          </div>

          <div className="stat">
            <div className="stat-label">Righe</div>
            <div className="stat-value num">{lines.length}</div>
            <div className="stat-note">{lines.filter((l) => l.totalQuantity > 0).length} con quantità</div>
          </div>

          <div className="stat">
            <div className="stat-label">Righe modificate</div>
            <div className="stat-value num">{changed.length}</div>
            <div className="stat-note">in questa sessione</div>
          </div>
        </div>

        {!compatta && (
          <ThresholdProgress
            percent={validation.thresholdPercent}
            missingLabel={
              header.channel === 'Cedi' ?
                'Soglia in colli non impostata per questo punto vendita: nessun minimo da raggiungere.'
              : 'Minimo d’ordine non impostato per questo fornitore.'
            }
          />
        )}
      </Card>
      </div>

      <Card
        title="Righe della proposta"
        actions={
          savingItem !== undefined ?
            <span className="muted">Salvataggio in corso…</span>
          : editable ?
            <div className="card-actions">
              <span className="muted">Le quantità si salvano uscendo dal campo o premendo Invio.</span>
              <ContainerPanel
                proposalId={proposalId}
                onPoured={(updated) => {
                  setDetail(updated)
                  setRevision((r) => r + 1)
                }}
              />
              <AddLinePanel
                proposalId={proposalId}
                storeCode={header.store}
                supplierCode={header.supplier}
                isCedi={header.channel === 'Cedi'}
                onAdded={(updated) => {
                  setDetail(updated)
                  setRevision((r) => r + 1)
                }}
              />
            </div>
          : <span className="muted">Proposta chiusa: le quantità sono in sola lettura.</span>
        }
      >
        {lines.length === 0 ?
          <Empty>
            La proposta non ha righe: il motore di previsione non ha suggerito articoli per questo
            fornitore.
          </Empty>
        : <div className="table-wrap grid-scroll">
            <table className="dense">
              <GridHead columns={columns} grid={grid} extraColumns={editable ? 1 : 0} />
              <tbody>
                {grid.rows.map((line) => {
                  const problem = lineProblems[line.item]
                  const busy = savingItem === line.item

                  return (
                    <tr key={line.item} className={changed.includes(line.item) ? 'row-changed' : undefined}>
                      <td className="num">{line.item}</td>
                      <td>
                        {line.description}
                        {line.isManual && (
                          <>
                            {' '}
                            <Badge tone="info">manuale</Badge>
                          </>
                        )}
                        {problem && <div className="cell-problem">{problem}</div>}
                      </td>
                      <td className="num">
                        <Num value={line.packSize} />
                      </td>
                      <td className="num">
                        <Num value={line.soldLast10Days} />
                      </td>
                      <td className="num">
                        <Num value={line.storeStock} />
                      </td>
                      {showCediStock && (
                        <td className="num">
                          <Num value={line.cediStock} />
                        </td>
                      )}
                      <td className="num">
                        <Num value={line.incomingQuantity} />
                      </td>
                      <td className="num muted">
                        <Num value={line.suggestedQuantity} />
                      </td>
                      <td className="num">
                        <QuantityInput
                          value={line.requestedQuantity}
                          revision={revision}
                          // Il server dichiara in anticipo se questo campo è modificabile:
                          // meglio disabilitarlo che lasciare scoprire la regola sbagliando.
                          disabled={!editable || busy || !validation.requestedQuantityEditable}
                          title={
                            validation.requestedQuantityEditable ?
                              'Quantità calcolata dal motore, modificabile perché questo fornitore o punto vendita è escluso dalla previsione.'
                            : 'Calcolata dal motore di previsione: modificabile solo per i fornitori e i punti vendita esclusi dalla previsione.'
                          }
                          onCommit={(v) => void saveLine(line.item, { requestedQuantity: v })}
                        />
                      </td>
                      <td className="num">
                        <QuantityInput
                          value={line.addedQuantity}
                          revision={revision}
                          disabled={!editable || busy}
                          title="Quantità aggiunta a quella suggerita dal motore."
                          onCommit={(v) => void saveLine(line.item, { addedQuantity: v })}
                        />
                      </td>
                      <td className="num muted">
                        <Num value={line.specialQuantity} />
                      </td>
                      <td className="num">
                        <strong>
                          <Num value={line.totalQuantity} />
                        </strong>
                      </td>
                      <td className="num">{formatPacks(line.packCount)}</td>
                      {editable && (
                        <td className="num">
                          {/* Solo le righe aggiunte a mano si tolgono. Quelle del motore si
                              azzerano: cancellarle sembrerebbe una decisione, ma al giro
                              successivo tornerebbero identiche. */}
                          {line.isManual && (
                            <button
                              type="button"
                              className="row-remove"
                              title="Togli questa riga dalla proposta"
                              aria-label={`Togli ${line.description}`}
                              disabled={removingItem === line.item || busy}
                              onClick={() => void removeLine(line.item)}
                            >
                              ×
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <GridSummary grid={grid} total={lines.length} />
          </div>
        }
      </Card>
    </>
  )
}

/**
 * Campo quantità che salva quando perde il fuoco o con Invio, mai a ogni tasto: ogni salvataggio
 * è una chiamata al server e una rilettura, e a ogni tasto sarebbe insostenibile.
 *
 * `revision` cambia a salvataggio concluso e riporta nel campo il valore registrato dal server —
 * che può differire da quello digitato, per l'arrotondamento a confezione o per un rifiuto.
 */
function QuantityInput({
  value,
  revision,
  disabled,
  title,
  onCommit,
}: {
  value: number
  revision: number
  disabled?: boolean
  title?: string
  onCommit: (value: number) => void
}) {
  const [draft, setDraft] = useState(String(value))

  useEffect(() => {
    setDraft(String(value))
  }, [value, revision])

  const commit = (testo = draft) => {
    const parsed = Number(testo)
    if (!Number.isFinite(parsed) || parsed < 0 || Math.trunc(parsed) === value) {
      setDraft(String(value))
      return
    }
    onCommit(Math.trunc(parsed))
  }

  /**
   * Salvataggio mentre si scrive, dopo una pausa di un secondo.
   *
   * <p>Uscire dal campo restava il momento del salvataggio, e per una tastiera va benissimo: si
   * scrive e si passa alla riga dopo. Ma un giro di aggiunte su una proposta lunga dura minuti, e
   * chi lo interrompe — una telefonata, il portatile che si chiude, una finestra chiusa per
   * sbaglio — perdeva l'ultimo numero digitato senza sapere quale. Con la pausa, quello che si
   * vede sullo schermo è già sul server.</p>
   *
   * <p>Un secondo e non meno: sotto quella soglia si salverebbero le cifre intermedie di un
   * numero (il «1» e il «12» prima del «120»), e ogni cifra è una richiesta e un arrotondamento
   * alla confezione. La pausa si azzera a ogni tasto, quindi chi scrive di seguito manda una
   * richiesta sola.</p>
   */
  useEffect(() => {
    if (disabled) return
    if (draft === String(value)) return

    const attesa = setTimeout(() => commit(draft), 1000)
    return () => clearTimeout(attesa)
    // `commit` legge `draft` e `value`, entrambi già nelle dipendenze.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, value, disabled])

  return (
    <input
      className="qty-input"
      inputMode="numeric"
      value={draft}
      disabled={disabled}
      title={title}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') setDraft(String(value))
      }}
    />
  )
}
